import React, { useEffect, useContext } from 'react';
import AddToCartFormik from '@jetshop/core/cart/AddToCartFormik';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import t from '@jetshop/intl';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { useNotification } from '@jetshop/core/components/Notifications';
import InputWithLabel from '../../../components/Forms/Input';
import StockStatusIndicator from '../StockStatus/StockStatusIndicator';
import { useProductValidationMessage } from '../useProductValidationMessage';
import VariantSelector from '../../../components/VariantSelector/VariantSelector';
import addToCartMutation from '../../../gql/mutations/addToCart.gql';
import CartQuery from '../../../gql/queries/cartQuery.gql';
import AddToCartButton from '../../../components/ui/AddToCartButton';
import NotifyWhenBack from '../StockStatus/NotifyWhenBack';
import ProductToastWrapper from './ProductToast';
import ProductPrice from './ProductPrice';
import Styled from './Style';
import useDynamicPriceWithHistory from './useDynamicPriceWithHistory';

const AddToCartForm = ({
  product,
  variant,
  getMissingOptions,
  variantHandler
}) => {
  useEffect(() => {
    const script = document.createElement('script');
    script.text = 'window.Klarna?.OnsiteMessaging?.refresh();';
    script.async = true;
    script.crossOrigin = 'anonymous'; // Set the crossOrigin attribute
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [variantHandler]);

  const { selectedChannel } = useContext(ChannelContext);
  const culture = selectedChannel?.defaultLanguage?.culture;

  const price = useDynamicPriceWithHistory(product, variant);
  const stockStatus = useStockStatus(variant || product);
  const outOfStockNotify = stockStatus.status === 'notifyWhenBack';
  const { validation: variantValidation, missingOptions } = variantHandler;
  const { hasVariants } = product;
  const { validationMessage, enableValidation } = useProductValidationMessage({
    missingOptions,
    variantValidation,
    hasVariants,
    stockStatus
  });
  const [trigger, dismiss] = useNotification();

  const relatedProducts = product.relatedProducts;
  let status = stockStatus.status;
  var currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + 1);
  // Show yellow dot if product has expected deliveryDate later than now
  // When a product is in stock the date will (should) be reset
  if (status && new Date(stockStatus.stockDate) > currentDate) {
    status = 'soonInStock';
  }

  const onAddToCartInit = ({ mutationId, quantity, price }) => {
    trigger(
      <ProductToastWrapper
        selectedVariation={variant}
        product={product}
        quantity={quantity}
        price={price}
        relatedProducts={relatedProducts}
        dismiss={dismiss}
        notificationId={mutationId}
      />,
      {
        id: mutationId,
        type: 'add-to-cart',
        autoCloseAfter: 0
      }
    );
  };

  const onAddToCartError = () => {
    return ({ mutationId, quantity, price, error }) => {
      dismiss(mutationId);

      trigger(
        <ProductToastWrapper
          selectedVariation={variant}
          product={product}
          quantity={quantity}
          price={price}
          error={error}
        />,
        {
          type: 'add-to-cart'
        }
      );
    };
  };

  return (
    <AddToCartFormik
      onAddToCartInit={onAddToCartInit}
      onAddToCartError={onAddToCartError}
      cartQuery={CartQuery}
      cartMutation={addToCartMutation}
      product={product}
      variant={variant}
      getMissingOptions={getMissingOptions}
    >
      {() => {
        return (
          <>
            <ProductPrice price={price} />
            <StockStatusIndicator status={status} text={stockStatus.text} />
            {product.hasVariants && (
              <VariantSelector
                product={product}
                variantHandler={variantHandler}
                showValidation={!!validationMessage}
              />
            )}

            <Styled.CartForm>
              {product.customerComments &&
                product.customerComments.map(comment => (
                  <InputWithLabel
                    wrapperClassName="customer-comment-input-wrapper"
                    className="customer-comment-input"
                    data-testid={`${comment.name}-input`}
                    id={`comments['${comment.name}']`}
                    name={`comments['${comment.name}']`}
                    label={comment.name}
                    key={comment.name}
                  />
                ))}

              <AddToCartButton
                data-testid="add-to-cart"
                type="submit"
                disabled={!!validationMessage || outOfStockNotify}
                onClick={enableValidation}
                className={'add-to-cart'}
                style={{
                  opacity: !!validationMessage ? 0.8 : 1,
                  cursor: !!validationMessage ? 'not-allowed' : 'pointer',
                  width: '100%'
                }}
              >
                {validationMessage ? validationMessage : t('Add to cart')}
              </AddToCartButton>

              {outOfStockNotify && (
                <NotifyWhenBack
                  articleNumber={(variant || product).articleNumber}
                />
              )}
            </Styled.CartForm>
            <klarna-placement
              data-key="credit-promotion-auto-size"
              data-locale={culture}
              data-purchase-amount={(variant || product).price?.incVat * 100}
              class="KlarnaWrapper"
            />
          </>
        );
      }}
    </AddToCartFormik>
  );
};

export default AddToCartForm;
